import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider, SUPPORTED_LANGS, DEFAULT_LANG, detectLang } from "./i18n/I18nProvider";
import ErrorBoundary from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";
import LangBanner from "./components/LangBanner";
import SeoHead from "./components/SeoHead";
import Home from "./pages/Home";
import Housing from "./pages/Housing";
import HousingImmobilierWroclaw from "./pages/HousingImmobilierWroclaw";
import AcheterBienImmobilier from "./pages/AcheterBienImmobilier";
import RetraitePologne from "./pages/RetraitePologne";
import TravaillerPologne from "./pages/TravaillerPologne";
import CostOfLiving from "./pages/CostOfLiving";
import CityDetail from "./pages/CityDetail";
import ConfirmEmail from "./pages/ConfirmEmail";
import Unsubscribe from "./pages/Unsubscribe";
import ComingSoon from "./pages/ComingSoon";
import Demarches from "./pages/Demarches";
import DemarchesPaysTiers from "./pages/DemarchesPaysTiers";
import Entrepreneuriat from "./pages/Entrepreneuriat";
import Sante from "./pages/Sante";
import Blog from "./pages/Blog";
import BlogPostReformeTravail from "./pages/BlogPostReformeTravail";
import BlogPostPacteMigratoire from "./pages/BlogPostPacteMigratoire";
import BlogPostSanteSzpitalPoludniowy from "./pages/BlogPostSanteSzpitalPoludniowy";
import Connaissances from "./pages/Connaissances";
import ConnaissanceSujet from "./pages/ConnaissanceSujet";
import Legal from "./pages/Legal";
import Methodology from "./pages/Methodology";
import AdminSources from "./pages/AdminSources";
import AdminData from "./pages/AdminData";
import AdminNumbeo from "./pages/AdminNumbeo";
import AdminNumbeoEdit from "./pages/AdminNumbeoEdit";

function RootRedirect() {
  const target = detectLang() || DEFAULT_LANG;
  return <Navigate to={`/${target}/`} replace />;
}

function LegacyRedirect() {
  // /logement -> /fr/logement, /villes/x -> /fr/villes/x
  const { pathname, search } = useLocation();
  const target = detectLang() || DEFAULT_LANG;
  return <Navigate to={`/${target}${pathname}${search}`} replace />;
}

function LangShell() {
  return (
    <>
      <SeoHead />
      <LangBanner />
      <Navbar />
      <main>
        <Routes>
          <Route index element={<Home />} />
          <Route path="logement" element={<Housing />} />
          <Route path="logement/immobilier-wroclaw" element={<HousingImmobilierWroclaw />} />
          <Route path="acheter-bien-immobilier-pologne" element={<AcheterBienImmobilier />} />
          <Route path="retraite-pologne" element={<RetraitePologne />} />
          <Route path="travailler-pologne" element={<TravaillerPologne />} />
          <Route path="cout-de-la-vie" element={<CostOfLiving />} />
          <Route path="villes/:slug" element={<CityDetail />} />
          <Route path="confirmation" element={<ConfirmEmail />} />
          <Route path="desinscription" element={<Unsubscribe />} />
          <Route path="demarches" element={<Demarches />} />
          <Route path="demarches-pays-tiers" element={<DemarchesPaysTiers />} />
          <Route path="entrepreneuriat" element={<Entrepreneuriat />} />
          <Route path="sante" element={<Sante />} />
          <Route path="education" element={<ComingSoon sectionKey="education" />} />
          <Route path="blog" element={<Blog />} />
          <Route path="blog/travailler-pologne-reforme-2025" element={<BlogPostReformeTravail />} />
          <Route path="blog/pacte-migratoire-pologne" element={<BlogPostPacteMigratoire />} />
          <Route path="blog/sante-pologne-scandale-szpital-poludniowy" element={<BlogPostSanteSzpitalPoludniowy />} />
          <Route path="connaissances" element={<Connaissances />} />
          <Route path="connaissances/:rubriqueId/:sujetId" element={<ConnaissanceSujet />} />
          <Route path="connaissances/:rubriqueId" element={<Navigate to="/fr/connaissances" replace />} />
          <Route path="mentions-legales" element={<Legal type="mentions" />} />
          <Route path="confidentialite" element={<Legal type="privacy" />} />
          <Route path="cgu" element={<Legal type="cgu" />} />
          <Route path="methodologie" element={<Methodology />} />
          <Route path="admin/sources" element={<AdminSources />} />
          <Route path="admin/data" element={<AdminData />} />
          <Route path="admin/numbeo" element={<AdminNumbeo />} />
          <Route path="admin/numbeo/:slug" element={<AdminNumbeoEdit />} />
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </main>
      <Footer />
      <CookieBanner />
    </>
  );
}

function App() {
  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <I18nProvider>
          <ScrollToTop />
          <ErrorBoundary>
            <Routes>
            <Route path="/" element={<RootRedirect />} />
            {SUPPORTED_LANGS.map((l) => (
              <Route key={l} path={`/${l}/*`} element={<LangShell />} />
            ))}
            {/* Legacy paths without lang prefix - redirect */}
            <Route path="/logement" element={<LegacyRedirect />} />
            <Route path="/cout-de-la-vie" element={<LegacyRedirect />} />
            <Route path="/villes/:slug" element={<LegacyRedirect />} />
            <Route path="/confirmation" element={<LegacyRedirect />} />
            <Route path="/desinscription" element={<LegacyRedirect />} />
            <Route path="*" element={<RootRedirect />} />
            </Routes>
          </ErrorBoundary>
          <Toaster position="bottom-right" richColors />
        </I18nProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
